import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';

interface HeaderContainerProps {
  isScrolled: boolean;
}

interface LogoProps {
  isScrolled: boolean;
}

const HeaderContainer = styled.header<HeaderContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: ${props => props.isScrolled 
    ? 'rgba(255, 255, 255, 0.98)' 
    : 'linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 100%)'
  };
  backdrop-filter: ${props => props.isScrolled ? 'blur(20px) saturate(180%)' : 'none'};
  box-shadow: ${props => props.isScrolled ? '0 8px 32px rgba(0, 0, 0, 0.08)' : 'none'};
  z-index: 1000;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;

const Logo = styled.div<LogoProps>`
  font-size: 24px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${props => props.isScrolled ? '#111827' : '#ffffff'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: -0.02em;
  
  img {
    height: 36px;
    width: auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: ${props => props.isScrolled ? 'none' : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'};
    
    &:hover {
      transform: scale(1.05) translateY(-1px);
    }
  }

  div {
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: ${props => props.isScrolled ? '#111827' : '#ffffff'};
      transition: width 0.3s ease;
    }
    
    &:hover:after {
      width: 100%;
    }
  }
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  gap: 30px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li`
  a {
    color: #333;
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    transition: color 0.3s ease;
    
    &:hover {
      color: #1890ff;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 10px;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: white;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  
  li {
    margin: 15px 0;
    
    a {
      color: #333;
      text-decoration: none;
      font-size: 18px;
      display: block;
      padding: 10px 0;
      
      &:hover {
        color: #1890ff;
      }
    }
  }
`;

const navItems = [
  {
    text: " 进入系统",
    href: "https://i.xlvolution.cn",
    target: "_blank",
    // title: "1",
  },
  {
    text: " 帮助文档",
    href: "http://docs.xlvolution.cn",
    target: "_blank",
    title: "请联系管理员要文档密码",
  },
];

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeaderContainer isScrolled={isScrolled}>
      <HeaderContent>
        <Logo isScrolled={isScrolled}>
          <img src="./logo.png" alt="logo" />
          <div>XLVolution</div>
        </Logo>

        <Nav>
          <NavList>
            {navItems.map((item) => (
              <NavItem key={item.href}>
                {isScrolled ? (
                  <a href={item.href} target={item.target} title={item.title}>
                    {item.text}
                  </a>
                ) : (
                  <a
                    href={item.href}
                    target={item.target}
                    title={item.title}
                    style={{ color: "#fff" }}
                  >
                    {item.text}
                  </a>
                )}
              </NavItem>
            ))}
          </NavList>
        </Nav>

        <MobileMenuButton
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
        </MobileMenuButton>

        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <ul>
              {navItems.map((item) => (
                <li key={item.href}>
                  <a href={item.href} target={item.target} title={item.title}>
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </MobileMenu>
        )}
      </HeaderContent>
    </HeaderContainer>
  );
};